"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@/hooks/use-translations";

import { readConsent, writeConsent } from "@/lib/analytics-consent";
import { isCapacitorApp } from "@/lib/utils";
import { initPostHog } from "@/lib/posthog";

/**
 * Asks before anything is measured.
 *
 * Shown only when the member has never answered. A "no" is remembered and never
 * asked again, because nagging somebody who has declined is how consent banners
 * earned their reputation.
 *
 * Sits above the tab bar rather than covering it, and respects the safe area,
 * because in the native apps this renders inside a WebView with a home
 * indicator underneath it.
 */
export function AnalyticsConsentBanner() {
  const { t } = useTranslations("consent");
  const [visible, setVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    /* Never in the phone app.

       A website cookie banner in a native app is a web artefact that no other app has,
       and it was also sitting on top of the Sign up, Terms, Privacy and Cookie links at
       the bottom of the sign in screen.

       Consent is not being skipped, it is being asked somewhere that suits an app. The
       key this banner writes is the same one the Settings toggle writes
       (lib/analytics-consent.ts), and it defaults to OFF: with no answer recorded,
       readConsent() returns null, initPostHog() returns early and AnalyticsWithConsent
       renders nothing. So in the app nothing is measured until a member turns analytics
       on in Settings, which is the safe side of PECR rather than a shortcut around it.

       If in-app analytics is wanted later, the answer is a proper one-time in-app
       question during onboarding, not this. */
    if (isCapacitorApp()) return;

    /* After mount, so the server and the first client render agree and there is
       no hydration mismatch: the server cannot know what is in localStorage. */
    setVisible(readConsent() === null);
  }, []);

  /* Publish the banner's real height as --consent-banner-h on <html>.
     Most pages scroll, so an overlay at the bottom costs them nothing. A screen that
     is deliberately exactly one viewport tall and does NOT scroll (the app's welcome
     screen) has no way to get out from under it, and the banner would sit on top of
     the "Create account" button with no way to reach it. Those screens reserve this
     much room at the bottom while the question is unanswered.

     Measured rather than hardcoded: the copy wraps to a different number of lines per
     locale, and the layout changes at the sm breakpoint. */
  useEffect(() => {
    const el = bannerRef.current;
    const root = document.documentElement;

    if (!visible || !el) {
      root.style.removeProperty("--consent-banner-h");
      return;
    }

    const publish = () => {
      root.style.setProperty("--consent-banner-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.removeProperty("--consent-banner-h");
    };
  }, [visible]);

  if (!visible) return null;

  function choose(choice: "granted" | "denied") {
    writeConsent(choice);
    setVisible(false);
    if (choice === "granted") {
      void initPostHog();
      /* Vercel Analytics is mounted conditionally on the same key higher up the
         tree, and it reads that key once on mount. A reload is the honest way to
         turn it on now rather than at the next navigation. */
      window.location.reload();
    }
  }

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-live="polite"
      aria-label={t("title")}
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[9998] px-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-2 md:px-6 md:pb-4"
    >
      {/* Dressed as the brand's deep surface, the same ground the dark sections and the
          auth panel stand on, so it reads as part of the site rather than a third-party
          bolt-on. The accept action is the brand blue; declining is quiet but equal. */}
      <div
        className="pointer-events-auto mx-auto flex max-w-3xl flex-col gap-2.5 rounded-2xl px-4 py-3 text-white shadow-[0_28px_60px_-24px_rgba(4,10,45,0.75)] sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:px-5"
        style={{ background: "linear-gradient(150deg, var(--color-deep) 0%, var(--color-deep-soft) 100%)" }}
      >
        <div className="min-w-0">
          <p className="text-[13px] font-semibold sm:text-sm">{t("title")}</p>
          <p className="mt-0.5 text-xs leading-snug text-white/70 sm:text-[13px]">
            {t("body")}{" "}
            <Link
              href="/privacy"
              /* `!` is needed because globals.css sets an unlayered `a { color: inherit }`,
                 which beats Tailwind's layered utilities whatever their specificity. */
              className="font-medium !text-white underline underline-offset-2"
            >
              {t("privacyLink")}
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="h-9 flex-1 rounded-full border border-white/25 px-4 text-sm font-medium text-white/85 transition-colors hover:bg-white/10 sm:flex-none sm:px-5"
          >
            {t("decline")}
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className="h-9 flex-1 rounded-full px-4 text-sm font-semibold text-white transition-colors sm:flex-none sm:px-5"
            style={{ background: "var(--color-primary)" }}
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
